/**
 * AuthorshipMeter — small colored badge showing a chapter's human-authorship %.
 *
 * Three states:
 *   compliant  (>= minPct + buffer)  → green
 *   warning    (>= minPct)           → amber
 *   non_compliant (< minPct)         → red
 *   no_baseline                      → grey dashed (chapter was hand-written, no AI baseline)
 *
 * Usage:
 *   <AuthorshipMeter pct={42} minPct={30} warnPct={40} size="sm" />
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { ShieldCheck, ShieldAlert, Shield, HelpCircle } from 'lucide-react';

const SIZES = {
  xs: 'text-[9px] px-1 py-0.5 gap-0.5',
  sm: 'text-[10px] px-1.5 py-0.5 gap-1',
  md: 'text-[11px] px-2 py-1 gap-1',
};

export function AuthorshipMeter({ pct, minPct = 30, warnPct = 40, hasBaseline = true, size = 'sm', className }) {
  if (!hasBaseline) {
    return (
      <span
        data-testid="authorship-meter-no-baseline"
        className={cn(
          'inline-flex items-center rounded font-medium border border-dashed',
          'border-border text-muted-foreground',
          SIZES[size] || SIZES.sm,
          className,
        )}
        title="No AI baseline recorded — chapter was hand-written or pre-imported"
      >
        <HelpCircle className="h-3 w-3 shrink-0" />
        No baseline
      </span>
    );
  }

  const isCompliant    = pct >= warnPct;
  const isWarning      = pct >= minPct && pct < warnPct;
  const isNonCompliant = pct < minPct;

  const colorClasses = isCompliant
    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30'
    : isWarning
    ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30'
    : 'bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/30';

  const Icon = isCompliant ? ShieldCheck : isWarning ? ShieldAlert : Shield;

  return (
    <span
      data-testid={`authorship-meter-${isCompliant ? 'compliant' : isWarning ? 'warning' : 'non-compliant'}`}
      className={cn(
        'inline-flex items-center rounded font-medium',
        colorClasses,
        SIZES[size] || SIZES.sm,
        className,
      )}
      title={`Human authorship: ${pct?.toFixed(1)}% (min: ${minPct}%)`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {pct?.toFixed(0)}% human
    </span>
  );
}
